import {
  Briefcase,
  Clock,
  Database,
  FileText,
  HardDrive,
  Home,
  Layers,
  LineChart,
  Presentation as PresentationIcon,
  Settings,
  Sliders,
  Sparkles,
  Tag,
  Upload,
  User,
  Workflow,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ComponentType, type SVGProps } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command.js';
import { useScenariosList } from '../features/scenario/hooks.js';
import { useAuth } from '../lib/auth-context.js';
import { getRecentScenarios, type RecentScenario } from '../lib/recent-scenarios.js';
import { isCustomerAdmin } from '../lib/roles.js';

/**
 * Cmd+K / Ctrl+K command palette. Mounts at app root. Indexes:
 *
 *   • Static navigation entries — every top-level route the sidebar
 *     exposes plus a few deep-links (scenarios by role, presentation,
 *     planning sub-pages).
 *   • Live scenario rows from `useScenariosList`. Selecting a scenario
 *     opens its detail modal via the `?scenarioId=` URL param the
 *     scenarios page already understands; selecting from any other
 *     route navigates to `/scenarios?scenarioId=...` first.
 *
 * Cmdk's built-in fuzzy ranking handles search.
 */
export interface NavCommand {
  kind: 'nav';
  id: string;
  label: string;
  hint?: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  group: string;
  /** Hidden from customer viewers (V-02); the route itself is gated too. */
  adminOnly?: boolean;
}

const NAV_COMMANDS: ReadonlyArray<NavCommand> = [
  { kind: 'nav', id: 'home', label: 'Home', to: '/', icon: Home, group: 'Navigate' },
  {
    kind: 'nav',
    id: 'dashboard',
    label: 'Daily Dashboard',
    to: '/dashboard',
    icon: LineChart,
    group: 'Navigate',
  },
  {
    kind: 'nav',
    id: 'scenarios',
    label: 'Scenarios · Business Executive',
    to: '/scenarios',
    icon: Briefcase,
    group: 'Scenarios',
  },
  {
    kind: 'nav',
    id: 'scenarios-admin',
    label: 'Scenarios · Workfront Administrator',
    to: '/scenarios/administrator',
    icon: Settings,
    group: 'Scenarios',
  },
  {
    kind: 'nav',
    id: 'scenarios-dev',
    label: 'Scenarios · Fusion Developer',
    to: '/scenarios/developer',
    icon: Zap,
    group: 'Scenarios',
  },
  {
    kind: 'nav',
    id: 'kpi-metrics',
    label: 'KPI Builder · Metrics',
    to: '/kpi',
    icon: Sliders,
    group: 'KPI',
  },
  {
    kind: 'nav',
    id: 'kpi-types',
    label: 'KPI Builder · Types',
    to: '/kpi/types',
    icon: Layers,
    group: 'KPI',
  },
  {
    kind: 'nav',
    id: 'value-metrics',
    label: 'Value · Metrics',
    to: '/value',
    icon: Sparkles,
    group: 'Value',
  },
  {
    kind: 'nav',
    id: 'value-scenario-kpis',
    label: 'Value · Scenario KPIs',
    to: '/value/scenario-kpis',
    icon: Workflow,
    group: 'Value',
  },
  {
    kind: 'nav',
    id: 'workfront-catalog',
    label: 'Workfront · Object Catalog',
    to: '/workfront',
    icon: Database,
    group: 'Workfront',
  },
  {
    kind: 'nav',
    id: 'workfront-custom-forms',
    label: 'Workfront · Custom Forms Migration',
    to: '/workfront/custom-forms-migration',
    icon: FileText,
    group: 'Workfront',
    adminOnly: true,
  },
  {
    kind: 'nav',
    id: 'planning-er',
    label: 'Planning · Schema reference',
    to: '/planning/er-diagram',
    icon: Database,
    group: 'Planning',
  },
  {
    kind: 'nav',
    id: 'planning-backup',
    label: 'Planning · Backup',
    to: '/planning/backup',
    icon: HardDrive,
    group: 'Planning',
    adminOnly: true,
  },
  {
    kind: 'nav',
    id: 'planning-restore',
    label: 'Planning · Restore',
    to: '/planning/restore',
    icon: Upload,
    group: 'Planning',
    adminOnly: true,
  },
  {
    kind: 'nav',
    id: 'planning-governance',
    label: 'Planning · Governance + Tags',
    to: '/planning/governance',
    icon: Tag,
    group: 'Planning',
  },
  {
    kind: 'nav',
    id: 'presentation',
    label: 'Presentation',
    to: '/presentation',
    icon: PresentationIcon,
    group: 'Other',
  },
  { kind: 'nav', id: 'user', label: 'User Settings', to: '/user', icon: User, group: 'Other' },
  {
    kind: 'nav',
    id: 'support',
    label: 'Support · Documentation',
    to: '/support',
    icon: FileText,
    group: 'Other',
  },
];

export function navCommandsFor(isAdmin: boolean): ReadonlyArray<NavCommand> {
  return isAdmin ? NAV_COMMANDS : NAV_COMMANDS.filter((c) => !c.adminOnly);
}

export function CommandPalette(): JSX.Element {
  const { session } = useAuth();
  const isAdmin = isCustomerAdmin(session);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: scenarioRows } = useScenariosList();
  // Reset of `recents` happens every open so newly-viewed entries
  // appear without the palette having to re-mount. Reading on every
  // open is cheap (localStorage hit + JSON.parse of ≤ 8 items).
  const [recents, setRecents] = useState<ReadonlyArray<RecentScenario>>(() => getRecentScenarios());
  useEffect(() => {
    if (open) setRecents(getRecentScenarios());
  }, [open]);

  // Cmd+K (Mac) / Ctrl+K (everyone else). `/` also opens when the
  // user isn't already focused in a text input — vim-style.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const k = e.key.toLowerCase();
      if (k === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (k === '/' && !isEditableTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const scenarios = useMemo(
    () =>
      (scenarioRows ?? [])
        .map((r) => {
          const fusionId = typeof r.fusionId === 'string' ? r.fusionId : '';
          const name = typeof r.name === 'string' ? r.name : '';
          const folderName = typeof r.folderName === 'string' ? r.folderName : '';
          return { fusionId, name, folderName };
        })
        .filter((s) => s.fusionId !== '' && s.name !== '')
        .slice(0, 50),
    [scenarioRows],
  );

  const openScenario = (fusionId: string): void => {
    const onScenariosPage = window.location.pathname.startsWith('/scenarios');
    if (onScenariosPage) {
      const next = new URLSearchParams(searchParams);
      next.set('scenarioId', fusionId);
      setSearchParams(next);
    } else {
      navigate(`/scenarios?scenarioId=${encodeURIComponent(fusionId)}`);
    }
    setOpen(false);
  };

  const grouped = useMemo(() => {
    const m = new Map<string, NavCommand[]>();
    for (const cmd of navCommandsFor(isAdmin)) {
      const arr = m.get(cmd.group);
      if (arr) arr.push(cmd);
      else m.set(cmd.group, [cmd]);
    }
    return [...m.entries()];
  }, [isAdmin]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages or scenarios… (Cmd/Ctrl+K)" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>
        {recents.length > 0 ? (
          <>
            <CommandGroup heading="Recent">
              {recents.map((r) => (
                <CommandItem
                  key={`recent-${r.fusionId}`}
                  value={`recent ${r.name} ${r.folderName ?? ''} ${r.fusionId}`}
                  onSelect={() => openScenario(r.fusionId)}
                >
                  <Clock className="mr-2 h-4 w-4 text-foreground/40" />
                  <span>{r.name}</span>
                  {r.folderName ? (
                    <span className="ml-2 truncate text-xs text-foreground/40">{r.folderName}</span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}
        {grouped.map(([group, cmds]) => (
          <CommandGroup key={group} heading={group}>
            {cmds.map((cmd) => (
              <CommandItem
                key={cmd.id}
                value={`${cmd.group} ${cmd.label}`}
                onSelect={() => {
                  navigate(cmd.to);
                  setOpen(false);
                }}
              >
                <cmd.icon className="mr-2 h-4 w-4 text-foreground/60" />
                <span>{cmd.label}</span>
                {cmd.hint ? (
                  <span className="ml-2 text-xs text-foreground/40">{cmd.hint}</span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        {scenarios.length > 0 ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Scenarios">
              {scenarios.map((s) => (
                <CommandItem
                  key={s.fusionId}
                  value={`scenario ${s.name} ${s.folderName} ${s.fusionId}`}
                  onSelect={() => openScenario(s.fusionId)}
                >
                  <Zap className="mr-2 h-4 w-4 text-[color:var(--brand-yellow)]" />
                  <span>{s.name}</span>
                  {s.folderName ? (
                    <span className="ml-2 truncate text-xs text-foreground/40">{s.folderName}</span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}
